import i18n from "../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Clock3, MapPin, LoaderCircle, AlignLeft } from "lucide-react";
import { toast } from "sonner";

function formatDue(value) {
    if (!value) return "No data";
    const text = String(value);
    if (!text.includes("T")) return text;
    const [datePart, timePart] = text.split("T");
    return timePart ? `${datePart} ${timePart.slice(0, 5)}` : datePart;
}

function completionValue(task) {
    return Number(task?.completion_pct ?? task?.progress ?? 0) || 0;
}

export default function WorkerPage() {
    const { user } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [busyId, setBusyId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const load = async () => {
        setLoading(true); setError("");
        try {
            const { data } = await api.get("/tasks/mine");
            setTasks(Array.isArray(data) ? data : []);
        } catch (requestError) {
            setTasks([]);
            setError(requestError.response?.status === 403 ? "You are not authorized to view these task assignments." : "Assigned tasks could not be loaded. Please try again.");
        } finally { setLoading(false); }
    };

    useEffect(() => {
        load();
    }, []);

    const sortedTasks = useMemo(() => {
        return [...tasks].sort((a, b) => {
            const aDate = a.due_date || "9999-12-31";
            const bDate = b.due_date || "9999-12-31";
            if (aDate !== bDate) return aDate.localeCompare(bDate);
            return completionValue(a) - completionValue(b);
        });
    }, [tasks]);

    if (loading) return <div className="space-y-4" data-testid="employee-tasks-page"><Card className="p-6 text-muted-foreground" data-testid="tasks-loading-state">{i18n.t("batch4.777595adaace")}</Card></div>;
    if (error) return <div className="space-y-4" data-testid="employee-tasks-page"><Card className="p-4 border-destructive/40 text-destructive" role="alert" data-testid="tasks-error-state">{error}</Card><Button variant="outline" onClick={load}>{i18n.t("batch4.042c862e4467")}</Button></div>;

    const completeTask = async (task) => {
        setBusyId(task.id);
        try {
            await api.post(`/tasks/${task.id}/complete`, { checklist: task.checklist });
            toast.success("Task marked complete");
            await load();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Could not complete task");
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="space-y-4" data-testid="employee-tasks-page">
            <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground">{i18n.t("batch4.1b0a77e6bbea")}</div>
                <h1 className="text-3xl font-bold tracking-tight">{user?.name || "Employee"}</h1>
                <p className="text-sm text-muted-foreground mt-1">{i18n.t("batch4.b9f456e8b0f9")}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <Card className="p-4">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{i18n.t("batch4.e24e824b6811")}</div>
                    <div className="mt-2 text-2xl font-bold">{sortedTasks.length}</div>
                </Card>
                <Card className="p-4">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{i18n.t("batch4.1798b3ba42ee")}</div>
                    <div className="mt-2 text-2xl font-bold">{sortedTasks.filter((task) => ["completed", "verified"].includes(String(task.status || "").toLowerCase())).length}</div>
                </Card>
            </div>

            <div className="space-y-3">
                {sortedTasks.map((task) => {
                    const pct = completionValue(task);
                    const status = String(task.status || "not_started").replace(/_/g, " ");
                    return (
                        <Card key={task.id} className="p-4" data-testid={`employee-task-${task.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <h2 className="text-lg font-semibold leading-tight">{task.title || "Untitled task"}</h2>
                                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                                        <Badge variant="outline" className="font-medium">{task.priority || "No data"}</Badge>
                                        <Badge variant="outline" className="font-medium">{task.area || "Not assigned"}</Badge>
                                        <Badge variant="outline" className="font-medium">{status}</Badge>
                                    </div>
                                </div>
                                <div className="text-right text-xs text-muted-foreground shrink-0">
                                    <div className="flex items-center gap-1 justify-end"><Clock3 className="w-3.5 h-3.5" />{formatDue(task.due_date)}</div>
                                    <div className="mt-1">{pct}%</div>
                                </div>
                            </div>

                            {task.description ? (
                                <div className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
                                    <AlignLeft className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>{task.description}</span>
                                </div>
                            ) : null}

                            <div className="mt-3">
                                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                    <span>{i18n.t("batch4.1b90271d66cf")}</span>
                                    <span>{pct}%</span>
                                </div>
                                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                                    <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }} />
                                </div>
                            </div>

                            <div className="mt-3 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{task.area || "Not assigned"}</span>
                                <span>{task.description ? "Description available" : "No description"}</span>
                            </div>

                            {!["completed", "verified"].includes(String(task.status || "").toLowerCase()) ? (
                                <Button
                                    onClick={() => completeTask(task)}
                                    className="h-12 w-full mt-3"
                                    disabled={busyId === task.id}
                                    data-testid={`complete-task-${task.id}`}
                                >
                                    {busyId === task.id ? <LoaderCircle className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                                    {i18n.t("batch4.1f5a1abf2f08")}
                                </Button>
                            ) : (
                                <div className="mt-3 rounded-xl bg-primary/10 text-primary text-sm text-center py-3 font-medium">{i18n.t("batch4.1798b3ba42ee")}</div>
                            )}
                        </Card>
                    );
                })}
                {sortedTasks.length === 0 && <Card className="p-8 text-center text-muted-foreground">{i18n.t("batch4.dcb45acec258")}</Card>}
            </div>
        </div>
    );
}
